/**
 * Predicate helpers for `CodeoSkill.documentUsesSkill`. Err toward passing: a false pass costs
 * nothing, a false failure blocks a correct composition behind a lint error it cannot clear.
 */
export type DocumentUsesSkill = (html: string) => boolean;

function scriptSource(html: string): string {
    return [...html.matchAll(/<script(?![^>]*\bsrc=)[^>]*>([\s\S]*?)<\/script>/g)]
        .map((match) => match[1])
        .join('\n');
}

function markupSource(html: string): string {
    return html.replace(/<script[\s\S]*?<\/script>/g, ' ');
}

export function countOf(text: string, pattern: RegExp): number {
    return (text.match(pattern) ?? []).length;
}

/**
 * Checks ask this rather than "are there at least N of them". N was a guess about how many items a
 * graphic would hold, and a plan calling for fewer made a faithful composition unbuildable: a
 * two-word headline built from per-word `.kw` spans is still a word-by-word reveal, and no edit can
 * give it a third word. Plurality is what the techniques require; the count is not.
 */
export function repeatsAClass(markup: string, tag = ''): boolean {
    return largestClassGroup(markup, tag) >= 2;
}

export function largestClassGroup(markup: string, tag = ''): number {
    const opening = tag ? new RegExp(`<${tag}\\b[^>]*>`, 'g') : /<[a-zA-Z][^>]*>/g;
    const counts = new Map<string, number>();
    for (const element of markup.match(opening) ?? []) {
        const classes = /\bclass\s*=\s*["']([^"']*)["']/.exec(element)?.[1] ?? '';
        for (const name of classes.split(/\s+/).filter(Boolean)) {
            counts.set(name, (counts.get(name) ?? 0) + 1);
        }
    }
    return Math.max(0, ...counts.values());
}

export function beatCount(script: string): number {
    return countOf(script, /\.\s*(?:to|from|fromTo|set|add|call)\s*\(/g);
}

export type TweenCall = {
    /** `to`, `fromTo`, `from`, `set` or `add`, as written. */
    method: string;
    chunk: string;
};

/**
 * Split rather than parsed: a chunk runs to the next insertion, which is enough to tell "this
 * element shrinks as it fades" from "something shrinks and something else fades".
 *
 * The method is carried alongside because the same vars mean opposite things depending on it: in a
 * `from` they are where the tween starts, so `opacity: 0` there is an entrance, while in a `to` it
 * is an exit. A check that reads the vars without the method reads every `from` entrance backwards.
 */
export function tweenCalls(script: string): TweenCall[] {
    const parts = script.split(/\.\s*(to|fromTo|from|set|add)\s*\(/);
    const calls: TweenCall[] = [];
    for (let index = 1; index < parts.length; index += 2) {
        const chunk = parts[index + 1] ?? '';
        if (chunk.includes('{')) {
            calls.push({ method: parts[index], chunk });
        }
    }
    return calls;
}

export function tweenChunks(script: string): string[] {
    return tweenCalls(script).map((call) => call.chunk);
}

export function someTween(script: string, ...tests: RegExp[]): boolean {
    return tweenChunks(script).some((chunk) => tests.every((test) => test.test(chunk)));
}

export function tweensByTarget(script: string): Map<string, string> {
    const byTarget = new Map<string, string>();
    for (const chunk of tweenChunks(script)) {
        const target = /^\s*['"]([^'"]+)['"]/.exec(chunk)?.[1];
        if (target) {
            byTarget.set(target, `${byTarget.get(target) ?? ''}\n${chunk}`);
        }
    }
    return byTarget;
}

export function someTarget(script: string, ...tests: RegExp[]): boolean {
    return [...tweensByTarget(script).values()].some((tweens) =>
        tests.every((test) => test.test(tweens)),
    );
}

/** Compositions commonly write `.48` and `duration: SPAN`, so the leading digit is optional and
 *  named constants simply do not appear here. */
export function valuesOf(script: string, property: string): number[] {
    const pattern = new RegExp(`\\b${property}:\\s*(-?(?:\\d+(?:\\.\\d+)?|\\.\\d+))`, 'g');
    return [...script.matchAll(pattern)].map((match) => Number(match[1]));
}

export function busiestTarget(script: string): number {
    const counts = new Map<string, number>();
    for (const [, selector] of script.matchAll(/['"]([#.][A-Za-z][\w-]*)['"]/g)) {
        counts.set(selector, (counts.get(selector) ?? 0) + 1);
    }
    return Math.max(0, ...counts.values());
}

export function hasStrokeDraw(script: string): boolean {
    return (
        /strokeDashoffset|stroke-dashoffset/.test(script) &&
        /getTotalLength|getComputedTextLength|Math\.PI/.test(script)
    );
}

export function hasEasedMotion(script: string): boolean {
    return /ease:\s*['"](?:power|back|sine|expo|elastic|circ|steps)/.test(script);
}

/**
 * Whether one treatment reaches several elements. A stagger says so outright, and so does a loop —
 * written `forEach`, `map`, `for (… of …)` or an index walk, according to taste. The technique is
 * the same in each, so reading for one spelling turns a check into a quiz about style.
 */
export function treatsSeveral(script: string): boolean {
    return /\bstagger\b|\bforEach\b|\.map\(|\bfor\s*\(|\bwhile\s*\(/.test(script);
}

/**
 * A transform-origin set away from the default centre — what makes a bar grow from its base or a
 * rule sweep from its start rather than opening from the middle. `bottom center` and `50% 100%`
 * are the same instruction, so both count.
 */
export function anchoredOffCentre(text: string): boolean {
    for (const [, value] of text.matchAll(/transform-?[Oo]rigin['"\s:]+([^;'"},)]+)/g)) {
        // `center`, `centre` and `50%` all name the middle, so compare on one spelling of it.
        const axes = value.trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (axes.length === 0) {
            continue;
        }
        const middle = axes.map((axis) => (/^(?:center|centre)$/.test(axis) ? '50%' : axis));
        if (middle.some((axis) => axis !== '50%')) {
            return true;
        }
    }
    return false;
}

/**
 * Whether an axis is tweened at all, given a value or handed over as shorthand. A loop over a
 * table of offsets writes `{ x, duration }`, which no `x:` pattern can see — and that is the
 * spelling the pan technique's own example uses for every one of its moves.
 */
export function tweensAxis(script: string, axis: 'x' | 'y'): boolean {
    return new RegExp(`\\b${axis}\\s*(?::|[,}])`).test(script);
}

/** Text put on an element from script, whichever of the three properties carries it. */
export function writesText(script: string): boolean {
    return /\b(?:textContent|innerText|innerHTML)\b/.test(script);
}

/** A growing prefix of a string, however it is taken. */
export function takesAPrefix(script: string): boolean {
    return /\b(?:slice|substring|substr)\(/.test(script);
}

/** `y: -180` and `y: ROLL_DISTANCE` are the same instruction; only the first is a literal. */
export function movesOn(script: string, axis: 'x' | 'y', negativeOnly = false): boolean {
    const sign = negativeOnly ? '-' : '-?';
    return (
        // A non-zero literal. `y: 0` is where a thing rests, not somewhere it travels to.
        new RegExp(`\\b${axis}:\\s*${sign}0*[1-9]`).test(script) ||
        // A name, or an expression opening on one — `-(i * PITCH)` is the offset computed.
        new RegExp(`\\b${axis}:\\s*${sign}[(A-Za-z_$]`).test(script)
    );
}

/**
 * Something lengthened over time — tweened to a width or scaleX — rather than sized once by a `set`.
 * GSAP takes the width as a string as readily as a number: `'660px'`, `'100%'`, `'+=420'`.
 */
export function growsLonger(script: string): boolean {
    return tweenCalls(script).some(
        ({ method, chunk }) =>
            method !== 'set' &&
            /\b(?:width|scaleX):\s*['"`]?(?:\+=)?(?:0*[1-9]|0?\.\d*[1-9]|[(A-Za-z_$])/.test(chunk),
    );
}

/** A constant carries the distance as often as a literal does, so both forms count. */
export function hasLongTravel(script: string): boolean {
    return (
        valuesOf(script, 'x').some((value) => Math.abs(value) >= 300) ||
        valuesOf(script, 'y').some((value) => Math.abs(value) >= 300) ||
        /\b[xy]:\s*-?[A-Z_][A-Z_0-9]*\b/.test(script)
    );
}

export function blurRadii(script: string): number[] {
    return [...script.matchAll(/blur\(\s*(\d+(?:\.\d+)?)px\)/g)].map((match) => Number(match[1]));
}

export function svgCount(markup: string): number {
    return countOf(markup, /<svg\b/g);
}

export function svgShapeCount(markup: string): number {
    return countOf(markup, /<(?:path|rect|circle|ellipse|line|polygon|polyline)\b/g);
}

/**
 * A set can be authored as one `<svg>` per object or as one `<svg>` holding a `<g>` per object —
 * both read as several objects on screen, so counting `<svg>` elements alone would reject the
 * second arrangement for the shape of its container rather than for what it draws.
 */
export function drawnObjectCount(markup: string): number {
    return Math.max(svgCount(markup), largestClassGroup(markup, 'g'));
}

export function sharesADrawingSystem(markup: string): boolean {
    return (
        repeatsAClass(markup, 'svg') || repeatsAClass(markup, 'g') || /stroke-width/.test(markup)
    );
}

/** A curve made with `CustomEase.create('name', 'M0,0 C…')` that something goes on to use. */
export function usesACustomCurve(script: string): boolean {
    const created = [
        ...script.matchAll(/CustomEase\.create\(\s*['"]([^'"]+)['"]\s*,\s*['"]M\s*0\s*,\s*0\s*C/g),
    ];
    return created.some(([, name]) => script.split(name).length > 2);
}

/** A drop onto the floor and the rebounds off it: a y move on each of an `.in` and an `.out` ease,
 *  with more than one hop. A table of hops walked by a loop counts as several. */
export function fallsAndRebounds(script: string): boolean {
    const onY = tweenChunks(script).filter((chunk) => /\by\s*[:,}]/.test(chunk));
    const falls = onY.some((chunk) => /ease:\s*['"]\w+\.in['"]/.test(chunk));
    const rises = onY.some((chunk) => /ease:\s*['"]\w+\.out['"]/.test(chunk));
    return falls && rises && (onY.length >= 3 || treatsSeveral(script));
}

/** One element taken a short way along an axis and then the other way: `x: -BACK`, `x: TRAVEL`. */
export function windsUpBeforeMoving(script: string): boolean {
    return [...tweensByTarget(script).values()].some((tweens) =>
        (['x', 'y'] as const).some(
            (axis) =>
                new RegExp(`\\b${axis}:\\s*-\\s*(?:0*[1-9]|[(A-Za-z_$])`).test(tweens) &&
                new RegExp(`\\b${axis}:\\s*(?:0*[1-9]|[(A-Za-z_$])`).test(tweens),
        ),
    );
}

/** Horizontal and vertical travel carried by separate tweens, so each can take its own ease. */
export function splitsTheAxes(script: string): boolean {
    // A chunk runs on to the next call, so a comment naming the other axis would ride along.
    const chunks = tweenChunks(script).map((chunk) => chunk.replace(/\/\/.*$/gm, ''));
    const xOnly = chunks.some((chunk) => /\bx\s*:/.test(chunk) && !/\by\s*:/.test(chunk));
    const yOnly = chunks.some((chunk) => /\by\s*:/.test(chunk) && !/\bx\s*:/.test(chunk));
    return xOnly && yOnly;
}

function signChanges(signs: number[]): number {
    return signs.filter((sign, index) => index > 0 && sign !== signs[index - 1]).length;
}

/**
 * A value that swings back and forth past its rest — the drag, the swing past, the wobble. Read
 * from a property's successive values wherever they are written (one tween each, or a keyframes
 * list) and from array literals, since a keyframe track is as often written as `[0, -18, 35, -26]`.
 */
export function swingsPastRest(script: string): boolean {
    const byProperty = new Map<string, number[]>();
    for (const [, property, minus, value] of script.matchAll(
        /['"]?(--[\w-]+|[A-Za-z_$][\w$]*)['"]?\s*:\s*(-?)\s*(\d*\.?\d+|[A-Za-z_$(][\w$.]*)/g,
    )) {
        if (/^0*\.?0*$/.test(value)) {
            continue;
        }
        const signs = byProperty.get(property) ?? [];
        signs.push(minus ? -1 : 1);
        byProperty.set(property, signs);
    }
    if ([...byProperty.values()].some((signs) => signChanges(signs) >= 3)) {
        return true;
    }
    return [...script.matchAll(/\[([^[\]]*)\]/g)].some(([, list]) => {
        const numbers = list
            .split(',')
            .map((item) => Number(item.trim()))
            .filter((value) => Number.isFinite(value) && value !== 0);
        return signChanges(numbers.map(Math.sign)) >= 3;
    });
}

/** A squash or stretch pushed well past life: a scale on one axis of 2.2 or more, or 0.35 or less. */
const PUSHED_ASPECT_RATIO = 4;

/** One axis pushed far past life, or a pose whose width and height part by 4× (type's 2 × 0.5). */
export function pushesAPose(script: string): boolean {
    const pushedAxis = [...valuesOf(script, 'scaleX'), ...valuesOf(script, 'scaleY')].some(
        (value) => value >= 2.2 || (value > 0 && value <= 0.35),
    );
    return (
        pushedAxis ||
        tweenChunks(script).some((chunk) => {
            const [scaleX] = valuesOf(chunk, 'scaleX');
            const [scaleY] = valuesOf(chunk, 'scaleY');
            if (!scaleX || !scaleY) {
                return false;
            }
            return Math.max(scaleX / scaleY, scaleY / scaleX) >= PUSHED_ASPECT_RATIO;
        })
    );
}

/** A body that deforms and a second element that moves on y of its own. */
export function movesAPartWithTheBody(script: string): boolean {
    const tweens = [...tweensByTarget(script).values()];
    const moving = tweens.filter((target) => /\by\s*[:,}]/.test(target));
    return moving.length >= 2 && tweens.some((target) => /\bscale[XY]?\b/.test(target));
}

export function check(predicate: (script: string, markup: string, html: string) => boolean) {
    return (html: string): boolean => predicate(scriptSource(html), markupSource(html), html);
}
